import { Component, Fragment, JSX } from "react";

import { AccessReportsService } from "@app/access-reports";
import { AccessReportStatusEnum } from "@app/access-reports/dashboard";
import { EmployeeGroup } from "@app/access-reports/shared";
import { EmployeeGroupExample, EmployeeGroupExampleFileNameEnum } from "@app/access-reports/wizard";
import { Message, MessageType, NotificationContextProps, withNotificationContext } from "@app/notifications";
import { AccessReportsEmployeeGroupUploadStatusChangePayload } from "@app/notifications/message";
import { DragAndDropFileUploader, LoadingBox, LoadingSpinner } from "@app/shared";
import { QESAlert } from "@app/shared/qes-standards";
import { DragAndDropFileUploaderStateEnum } from "@app/shared/interfaces/dragAndDropUploader";

type AccessReportsWizardStepPopulationProps = NotificationContextProps & {
  employeeGroup: EmployeeGroup;
  employeeGroupExamples: EmployeeGroupExample[];
  employeeGroupUploadStatus: AccessReportStatusEnum;
  employeeGroupUploadMessage: string;
  setEmployeeGroupUploadStatus: (employeeGroupUploadStatus: AccessReportStatusEnum, employeeGroupUploadMessage: string) => void;
  setEmployeeGroupUploadValues: (employeeGroup: EmployeeGroup) => void;
}

interface AccessReportsWizardStepPopulationState {
  loading: boolean;
}

class AccessReportsWizardStepPopulation extends Component<AccessReportsWizardStepPopulationProps, AccessReportsWizardStepPopulationState> {
  private readonly accessReportsService = new AccessReportsService();

  constructor(props: AccessReportsWizardStepPopulationProps) {
    super(props);

    this.state = {
      loading: false,
    };
  }

  public async componentDidMount() {
    this.props.subscribe(this.handleNotifications);
  }

  public componentWillUnmount() {
    this.props.unsubscribe(this.handleNotifications);
  }

  public render() {
    return (
      <Fragment>
        <div className="row">
          <div className="col-12">
            <p className="copy-text-default mb0">
              Upload a Census File: Please Upload a .CSV or .XLSX file that includes the information needed. <span className="font-weight-bold text-danger">Do not upload a PII or PHI file.</span>
              {this.getEmployeeGroupExamples()}
            </p>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <LoadingBox
              show={this.state.loading}
            />

            <DragAndDropFileUploader
              apiKeyName="employeeGroupFile"
              title=""
              filetype=".csv,.xlsx"
              hasBeenSuccessful={!!this.props.employeeGroup}
              onUpload={this.onUpload}
              onUploadStateChange={this.onUploadStateChange}
              testId="access-reports-wizard__population--file-upload"
            />

            {!!this.props.employeeGroupUploadStatus && this.getEmployeeGroupStatus()}
          </div>
        </div>
      </Fragment>
    );
  }

  private getEmployeeGroupExamples = (): React.ReactNode => {
    if (!!this.props.employeeGroupExamples && this.props.employeeGroupExamples.length) {
      const employeeGroupExamplePrefix = " Preformatted template downloads: ";
      const employeeGroupExampleLink: React.ReactNode[] = [employeeGroupExamplePrefix];

      for (let i = 0; i < this.props.employeeGroupExamples.length; i++) {
        let title = "";

        switch (this.props.employeeGroupExamples[i].fileName) {
          case EmployeeGroupExampleFileNameEnum.CsvUploadSamples:
            title = "CSV";
            break;
          case EmployeeGroupExampleFileNameEnum.ExcelUploadSamples:
            title = "Excel";
            break;
          default:
            title = "Invalid File Name";
            console.error("Invalid Employee Group Example File Name");
            break;
        }

        if (i !== 0 && this.props.employeeGroupExamples.length > 1) {
          employeeGroupExampleLink.push(
            <span key={i}> and <a className="font-weight-bold" href={this.props.employeeGroupExamples[i].link} id={`access-reports-wizard__population--example-${i}`}>{title}</a></span>
          );
        } else {
          employeeGroupExampleLink.push(
            <a key={i} className="font-weight-bold" href={this.props.employeeGroupExamples[i].link} id={`access-reports-wizard__population--example-${i}`}>{title}</a>
          );
        }
      }

      return employeeGroupExampleLink;
    }
  }

  private getEmployeeGroupStatus = (): JSX.Element => {
    if (this.props.employeeGroupUploadStatus === AccessReportStatusEnum.Running) {
      return (
        <div className="d-flex justify-content-center">
          <LoadingSpinner />
          <h4 className="align-self-center header-s ml-2 mb0">
            Your file is being processed
          </h4>
        </div>
      );
    } else {
      let alertText = "";
      let alertType: "warning" | "info" | "danger" | "success";

      switch (this.props.employeeGroupUploadStatus) {
        case AccessReportStatusEnum.Canceled:
          alertText = "The processing of your file has been canceled."
          alertType = "warning";
          break;
        case AccessReportStatusEnum.Created:
          alertText = "The processing of your file has been created."
          alertType = "info";
          break;
        case AccessReportStatusEnum.Failed:
          alertText = "The processing of your file has failed.";
          if (this.props.employeeGroupUploadMessage !== null) {
            alertText += " " + this.props.employeeGroupUploadMessage;
          }
          alertType = "danger";
          break;
        case AccessReportStatusEnum.Succeeded:
          alertText = "Your file has been successfully processed.";
          if (this.props.employeeGroupUploadMessage !== null) {
            alertText += " " + this.props.employeeGroupUploadMessage;
          }
          alertType = "success";
          break;
        default:
          console.error(`Invalid Employee Group Status: ${this.props.employeeGroupUploadStatus}`);
          break;
      }

      return (
        <QESAlert classNames="mb-3" type={alertType} persist={true}>
          {alertText}
        </QESAlert>
      );
    }
  }

  private handleNotifications = async (message: Message): Promise<void> => {
    if (message?.notificationType === MessageType.AccessReportsEmployeeGroupUploadStatusChange) {
      const payload: AccessReportsEmployeeGroupUploadStatusChangePayload = message.payload as AccessReportsEmployeeGroupUploadStatusChangePayload;

      if (!!payload) {
        this.props.setEmployeeGroupUploadStatus(payload.status, payload.uploadMessage);

        if (payload.status === AccessReportStatusEnum.Succeeded) {
          const employeeGroup: EmployeeGroup = {
            clientId: payload.clientId,
            dateCreated: payload.dateCreated,
            employeeGroupId: payload.employeeGroupId,
            employeeGroupStatus: payload.employeeGroupStatus,
            employeeGroupType: payload.employeeGroupType,
            fileName: payload.fileName,
            userId: payload.userId,
          }

          this.props.setEmployeeGroupUploadValues(employeeGroup);
        }
      }
    }
  }

  public onUpload = async (data: FormData): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      this.setState({
        loading: true,
      }, async () => {
        try {
          const employeeGroupId: number = await this.accessReportsService.createEmployeeGroupFromFile(data);

          if (!!employeeGroupId.toString()) {
            this.props.setEmployeeGroupUploadStatus(AccessReportStatusEnum.Running, null);
          }

          resolve();
        } catch {
          reject();
        }

        this.setState({
          loading: false,
        });
      });
    });

  }

  public onUploadStateChange = (uploadState: DragAndDropFileUploaderStateEnum) => {
    if (uploadState === DragAndDropFileUploaderStateEnum.Pending) {
      this.props.setEmployeeGroupUploadValues(null);
      this.props.setEmployeeGroupUploadStatus(null, null);
    }
  }
}

const WrappedAccessReportsWizardStepPopulation = withNotificationContext(AccessReportsWizardStepPopulation);

export default WrappedAccessReportsWizardStepPopulation as React.ComponentClass<AccessReportsWizardStepPopulationProps, AccessReportsWizardStepPopulationState>;
